import React from 'react';
import { whyChooseFeatures, processSteps, serviceAreas } from '../mock';
import { MapPin, Award, Leaf, ThumbsUp, Package, Wrench } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';

const iconMap = {
  MapPin,
  Award,
  Leaf,
  ThumbsUp,
  Package,
  Wrench
};

const WhyChoose = () => {
  return (
    <>
      {/* Why Choose Section */}
      <section className="py-20 bg-gradient-to-b from-zinc-900 to-black relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              Why Choose <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">Boss Detailing Pros?</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              We go beyond cleaning – we restore, protect, and enhance your vehicle with expert care. Our mobile detailing service ensures top-tier results at your convenience!
            </p>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {whyChooseFeatures.map((feature, index) => {
              const Icon = iconMap[feature.icon];
              return (
                <Card
                  key={feature.id}
                  className="group bg-zinc-800/50 border-zinc-700 hover:border-orange-500 p-6 text-center transform hover:scale-105 hover:-translate-y-2 transition-all duration-500 cursor-pointer"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className="flex flex-col items-center space-y-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white group-hover:text-orange-500 transition-colors duration-300">
                      {feature.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* CTA */}
          <div className="mt-16 text-center space-y-6">
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-8 py-6 text-lg rounded-md transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-orange-500/50"
                onClick={() => document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Book Now
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-orange-500 text-white hover:bg-orange-500 hover:text-white font-bold px-8 py-6 text-lg rounded-md transform hover:scale-105 transition-all duration-300"
                onClick={() => window.location.href = 'tel:9162993189'}
              >
                Call (916) 299-3189
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-black relative overflow-hidden">
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-white">
              The Boss Detailing <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">Experience</span>
            </h2>
          </div>

          {/* Process Steps */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div
                key={step.id}
                className="group relative"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                {/* Connecting Line */}
                {index < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-16 left-1/2 w-full h-0.5 bg-gradient-to-r from-orange-500 to-transparent"></div>
                )}
                
                <div className="relative bg-zinc-800/50 border border-zinc-700 hover:border-orange-500 rounded-xl p-6 transform hover:scale-105 transition-all duration-500 cursor-pointer">
                  {/* Number Circle */}
                  <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full flex items-center justify-center shadow-lg group-hover:scale-125 transition-transform duration-300">
                      <span className="text-white font-bold text-xl">{step.number}</span>
                    </div>
                  </div>

                  <div className="mt-8 text-center space-y-3">
                    <h3 className="text-xl font-bold text-white group-hover:text-orange-500 transition-colors duration-300">
                      {step.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-16 text-center">
            <Button
              size="lg"
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-8 py-6 text-lg rounded-md transform hover:scale-105 transition-all duration-300 shadow-xl hover:shadow-orange-500/50"
              onClick={() => document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Book Your Detailing Now!
            </Button>
          </div>
        </div>
      </section>

      {/* Service Areas Section */}
      <section className="py-20 bg-zinc-900 relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Map Image */}
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl border border-zinc-700">
                <img
                  src="https://images.unsplash.com/photo-1524661135-423995f22d0b?w=800"
                  alt="Service Area Map"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                
                {/* Floating Location Markers */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-pulse">
                    <MapPin className="w-16 h-16 text-orange-500 drop-shadow-lg" />
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Content */}
            <div className="space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold text-white">
                Serving Sacramento & <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">Beyond</span>
              </h2>
              <p className="text-xl text-gray-400">
                Expert mobile detailing brought to your doorstep – wherever you are!
              </p>

              <div>
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                  <MapPin className="w-6 h-6 text-orange-500" />
                  Our Service Areas Include:
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {serviceAreas.map((area, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 text-gray-300 hover:text-orange-500 transition-colors duration-300 cursor-pointer group"
                    >
                      <div className="w-2 h-2 bg-orange-500 rounded-full group-hover:scale-150 transition-transform duration-300"></div>
                      <span className="font-medium">{area}</span>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-gray-400 leading-relaxed">
                Whether you're at home, work, or anywhere in the region, we provide top-quality detailing services for your convenience.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button
                  size="lg"
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-8 py-4 rounded-md transform hover:scale-105 transition-all duration-300"
                  onClick={() => document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Book Now
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-orange-500 text-white hover:bg-orange-500 hover:text-white font-bold px-8 py-4 rounded-md transform hover:scale-105 transition-all duration-300"
                  onClick={() => window.location.href = 'tel:9162993189'}
                >
                  Call (916) 299-3189
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default WhyChoose;
